import { memo } from 'react';
import { AlertTriangle, Mail, ShieldAlert, UserCheck } from 'lucide-react';
import { Button, MetricTile, Skeleton, StatusMessage } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';

import { MEMBER_STAT } from '../../constants/memberStats';

const CARD_STYLE = Object.freeze({
  [MEMBER_STAT.ACTIVE]: { icon: UserCheck, variant: 'green' },
  [MEMBER_STAT.PENDING]: { icon: Mail, variant: 'blue' },
  [MEMBER_STAT.ACTIVE_WITHOUT_MFA]: { icon: ShieldAlert, variant: 'amber' },
});

const CARD_IDS = Object.values(MEMBER_STAT);

/**
 * The three indicator cards above the Members filters: active members, pending invitations and
 * active members without MFA. Sub-copy is passed as `MetricTile` `helperText`.
 *
 * @param {object} props
 * @param {{ activeCount: number, totalCount: number, pendingCount: number, notSentCount: number,
 *   activeWithoutMfaCount: number }|null} [props.stats]
 * @param {boolean} [props.isLoading]
 * @param {string|null} [props.error]
 * @param {() => void} [props.onRetry]
 */
function MembersIndicators({ stats = null, isLoading = false, error = null, onRetry }) {
  const { t } = useTranslation('people-access');

  if (error && !stats) {
    return (
      <div
        data-testid="members-indicators"
        className="flex flex-col items-center rounded-xl border border-edge bg-surface pb-4"
      >
        <StatusMessage icon={AlertTriangle} title={t('members.stats.loadError')} subtitle={t('members.list.retryHint')} />
        <Button variant="secondary" size="sm" onClick={onRetry}>
          {t('members.stats.retry')}
        </Button>
      </div>
    );
  }

  const values = stats && {
    [MEMBER_STAT.ACTIVE]: stats.activeCount,
    [MEMBER_STAT.PENDING]: stats.pendingCount,
    [MEMBER_STAT.ACTIVE_WITHOUT_MFA]: stats.activeWithoutMfaCount,
  };
  const helpers = stats && {
    [MEMBER_STAT.ACTIVE]: t('members.stats.active.helper', { total: stats.totalCount }),
    [MEMBER_STAT.PENDING]: t('members.stats.pending.helper', { count: stats.notSentCount }),
    [MEMBER_STAT.ACTIVE_WITHOUT_MFA]: t('members.stats.activeWithoutMfa.helper'),
  };

  return (
    <section
      data-testid="members-indicators"
      aria-label={t('members.stats.ariaLabel')}
      aria-busy={isLoading || undefined}
      className="grid grid-cols-1 gap-3 md:grid-cols-3"
    >
      {CARD_IDS.map((id) => (
        <MetricTile
          key={id}
          data-testid={`members-stat-${id}`}
          label={t(`members.stats.${id}.label`)}
          value={
            isLoading || !values ? (
              <span data-testid="members-stat-skeleton" className="block py-1">
                <Skeleton width={56} height={28} />
              </span>
            ) : (
              values[id]
            )
          }
          helperText={isLoading || !helpers ? undefined : helpers[id]}
          icon={CARD_STYLE[id].icon}
          variant={CARD_STYLE[id].variant}
          tone="subtle"
          align="left"
        />
      ))}
    </section>
  );
}

export default memo(MembersIndicators);
